// Shared Excel-upload report state, lifted verbatim from the norms upload
// modal (src/pages/ProjectScheduleManagement/norms/NormUploadModal.tsx) so
// every stage-machine upload type parses the fetchReportById record the same
// way. `totalRecords` is the only addition: the backend paginates
// convertedJson, so rows may be just the first page.

export interface UploadReportState {
  reportId: number | null;
  status: string;
  uploadStatus: string;
  totalRows: number;
  successCount: number;
  failureCount: number;
  rows: any[];
  topError: string;
  /** Total rows across all pages (rows holds only the fetched page). */
  totalRecords: number;
  /** Live "1500/6202 rows validated" text — only helpers that opt in write it. */
  progressText: string;
}

export const emptyReport: UploadReportState = {
  reportId: null,
  status: "",
  uploadStatus: "",
  totalRows: 0,
  successCount: 0,
  failureCount: 0,
  rows: [],
  topError: "",
  totalRecords: 0,
  progressText: "",
};

export const parseReport = (record: any): UploadReportState => {
  let rows: any[] = [];
  try {
    const raw = record?.convertedJson;
    rows = typeof raw === "string" ? JSON.parse(raw) : raw || [];
    if (!Array.isArray(rows)) rows = [];
  } catch {
    rows = [];
  }
  return {
    reportId: record?.reportId ?? null,
    status: record?.status || "",
    uploadStatus: record?.uploadStatus || "",
    totalRows: Number(record?.totalRows) || 0,
    successCount: Number(record?.successCount) || 0,
    failureCount: Number(record?.failureCount) || 0,
    rows,
    topError:
      typeof record?.error === "string" && record?.error !== "[]"
        ? record.error
        : "",
    totalRecords: Number(record?.pagination?.totalRecords) || 0,
    progressText: record?.progressText || "",
  };
};

// Terminal-state predicates (backend status lifecycle):
// validation done  → uploadStatus 'Preview' + status 'Processed'
// creation done    → uploadStatus 'Submit'  + status 'Processed'
// terminal failure → status 'Error' | 'Failed'
export const isValidationDone = (report: UploadReportState): boolean =>
  report.uploadStatus === "Preview" && report.status === "Processed";

export const isCreationDone = (report: UploadReportState): boolean =>
  report.uploadStatus === "Submit" && report.status === "Processed";

export const isTerminalFailure = (report: UploadReportState): boolean =>
  ["Error", "Failed"].includes(report.status);
